import { II18nextPrefix } from 'types/components/components.interface';

import { Form, Formik, FormikConfig } from 'formik';
import { useTranslation } from 'react-i18next';
import { IFormikProps, IFormProps } from '../types';

import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';

import { Label } from 'components/atoms';
import { useParamId } from 'hooks/useParamId';
import { PiEye, PiThumbsDown, PiThumbsUp } from 'react-icons/pi';
import { IChannelVideoForm } from 'services/channel/types/query';
import { array, object } from 'yup';
import { settlementInfoContractValidation } from '../music-product-digital/MusicProductDigitalForm';
import ChannelVideoContractsForm from './tabs/ChannelVideoContractsForm';
import ChannelVideoContractsFuture from './tabs/ChannelVideoContractsFuture';
import ChannelVideoContractsHistory from './tabs/ChannelVideoContractsHistory';
import ChannelVideoPlayer from './tabs/ChannelVideoPlayer';

import { useGetChannel } from 'services/channel';
import ChannelVideoAssetId from './tabs/ChannelVideoAssetId';

export const channelVideoInitialValues: IChannelVideoForm = {
  id: '',
  videoContracts: [],
  dateOfPublication: '',
  externalVideoId: '',
  videoImageLink: '',
  videoTitle: '',
  lastSyncDate: '',
  dislikesCount: 0,
  likesCount: 0,
  views: 0,
};

const validator = object().shape({
  videoContracts: array().of(settlementInfoContractValidation),
});

export interface IChannelVideoFormProps
  extends FormikConfig<IChannelVideoForm>,
    IFormProps,
    II18nextPrefix {}

const ChannelVideoForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = channelVideoInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: IChannelVideoFormProps) => {
  const channelId = useParamId('channel');
  const { t } = useTranslation();

  const channel = useGetChannel(channelId);

  return (
    <Formik
      {...props}
      validateOnChange
      validateOnBlur
      validateOnMount
      initialStatus={status}
      enableReinitialize
      validationSchema={validator}
      initialValues={initialValues}>
      {({
        setFieldValue,
        values,
        status,
        errors,
      }: IFormikProps<IChannelVideoForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status ? tittle : undefined}
              path={backPath ?? `channel/${channelId}/channel-video`}
              topbarContent={
                <div className=' w-full flex justify-end items-center gap-2'>
                  <Label color='blue' title='Views' size='compact' withText>
                    <PiEye /> {values.views.toLocaleString()}
                  </Label>
                  <Label title='Likes' size='compact' withText>
                    <PiThumbsUp /> {values.likesCount.toLocaleString()}
                  </Label>
                  {!!values.dislikesCount && (
                    <Label title='Dislikes' size='compact' withText>
                      <PiThumbsDown /> {values.dislikesCount.toLocaleString()}
                    </Label>
                  )}
                  {topbarContent}
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Image
                  size
                  fullResolutionUrl={values.videoImageLink}
                  editable={false}
                  onChange={file => setFieldValue('file', file)}
                  onDelete={
                    initialValues.videoImageLink
                      ? action => {
                          setFieldValue('videoImageLink', '');
                          setFieldValue('videoImageLink', '');
                          action.close();
                        }
                      : undefined
                  }
                />
                <InfoBox.Items columns={1}>
                  <FormInput.Text
                    displayMode={true}
                    name='externalVideoId'
                    label={t('input.label.externalId')}
                  />
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: 0,
                    name: 'contracts',
                    error: errors.videoContracts,
                    permissionName: 'ContractPermissionsLIST**',
                  },
                  {
                    value: 1,
                    name: 'player',
                  },
                  {
                    value: 2,
                    name: 'asset-id',
                  },
                ]}
                disabled={[
                  ...(disableTabs ?? []),
                  ...(channel.data?.settlementInfo?.contracts.length &&
                  channel.data?.settlementInfo.perVideoSettlement
                    ? []
                    : [0]),
                ]}
                i18nextPrefix={i18nextPrefix}
                displayMode={status?.displayMode}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

ChannelVideoForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'contracts', element: <ChannelVideoContractsForm /> },
    { path: 'player', element: <ChannelVideoPlayer /> },
    { path: 'asset-id', element: <ChannelVideoAssetId /> },
    { path: 'contracts/history', element: <ChannelVideoContractsHistory /> },
    { path: 'contracts/future', element: <ChannelVideoContractsFuture /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default ChannelVideoForm;
